import { html, LitElement, nothing } from "lit";
import { property } from "lit/decorators.js";

import { reset } from "../styles.js";

import { styles } from "./styles.js";
import { ifDefined } from "lit/directives/if-defined.js";

type ButtonVariant =
	| "primary"
	| "secondary"
	| "negative"
	| "negativeQuiet"
	| "utility"
	| "quiet"
	| "utilityQuiet"
	| "overlay"
	| "overlayInverted"
	| "overlayQuiet"
	| "overlayInvertedQuiet";

const variants = [
	"primary",
	"secondary",
	"negative",
	"negativeQuiet",
	"utility",
	"utilityQuiet",
	"quiet",
	"link",
	"overlay",
	"overlayInverted",
	"overlayQuiet",
	"overlayInvertedQuiet",
];

/**
 * Link component to use when creating links that look like buttons.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/link/frameworks/elements)
 */
class WarpLink extends LitElement {
	/** @internal */
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	/**
	 * Focus the link after it is rendered
	 */
	@property({ type: Boolean, reflect: true })
	autofocus = false;

	/**
	 * Visual style for the link/button.
	 *
	 * @throws {Error} Throws an error if an unsupported value is provided.
	 */
	@property({ reflect: true })
	variant: ButtonVariant | undefined;

	/**
	 * Render a smaller version
	 */
	@property({ type: Boolean, reflect: true })
	small = false;

	/**
	 * The URL the link points to
	 */
	@property({ reflect: true })
	href: string | undefined;

	/**
	 * Applies disabled styling, but you need to disable clicks on your own.
	 *
	 * The component renders an `<a>` element; `disabled` affects styling, but does not inherently prevent navigation. If you need to fully disable interaction, omit `href` and/or prevent default click behavior.
	 */
	@property({ type: Boolean, reflect: true })
	disabled = false;

	/**
	 * Where to display the linked URL (e.g. `_blank`)
	 */
	@property({ reflect: true })
	target: string | undefined;

	/**
	 * Relationship of the linked URL.
	 *
	 * If `target="_blank"` and `rel` is not provided, the component uses `noopener`.
	 */
	@property({ reflect: true })
	rel: string | undefined;

	/**
	 * Makes the link take up the full width of its parent
	 */
	@property({ attribute: "full-width", type: Boolean, reflect: true })
	fullWidth = false;

	static styles = [reset, styles];

	connectedCallback() {
		super.connectedCallback();

		// Validate variant if explicitly set
		if (this.variant && !variants.includes(this.variant)) {
			throw new Error(
				`Invalid "variant" attribute. Set its value to one of the following:\n${variants.join(", ")}.`,
			);
		}
	}

	firstUpdated() {
		if (this.autofocus) {
			setTimeout(() => this.focus(), 0);
		}
	}

	render() {
		return html`
			<a
				href="${this.href}"
				part="base"
				target="${this.target}"
				rel="${this.target === "_blank" ? this.rel || "noopener" : nothing}"
				tabindex="0"
				aria-description="${ifDefined(this.ariaDescription)}"
			>
				<slot></slot>
			</a>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"w-link": WarpLink;
	}
}

if (!customElements.get("w-link")) {
	customElements.define("w-link", WarpLink);
}

export { WarpLink };
